import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, IconButton, Tooltip } from '@mui/material';
import classNames from 'classnames';
import { Field, FieldProps, Form, Formik } from 'formik';
import { useCallback, useState } from 'react';
import * as yup from 'yup';
import { NumericInput } from '../../Input/NumericInput';
import { TaxBenefitFormValues } from '../TaxBenefitPage.hooks';

const INITIAL_VALUES: TaxBenefitFormValues = {
  irpContribution: 9_000_000,
  isaAnnualContribution: 10_000_000,
  isaType: 'general',
  expectedIsaProfit: 2_000_000,
  incomeLevel: 'below55m',
  projectionYears: 10,
};

const schema = yup.object().shape({
  irpContribution: yup.number().min(0, '0 이상이어야 합니다.').max(9_000_000, 'IRP 세액공제 한도는 연 900만 원입니다.').required('필수 입력 항목입니다.'),
  isaAnnualContribution: yup.number().min(0, '0 이상이어야 합니다.').max(20_000_000, 'ISA 연간 납입 한도는 2,000만 원입니다.').required('필수 입력 항목입니다.'),
  expectedIsaProfit: yup.number().min(0, '0 이상이어야 합니다.').required('필수 입력 항목입니다.'),
  projectionYears: yup.number().min(1, '1년 이상이어야 합니다.').max(30, '30년 이하로 입력해 주세요.').required('필수 입력 항목입니다.'),
});

function FieldTooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);

  return (
    <Tooltip open={open} onClose={() => setOpen(false)} title={text} arrow placement="top">
      <IconButton size="small" onClick={() => setOpen((v) => !v)} sx={{ p: 0.25, color: '#9ca3af' }}>
        <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
      </IconButton>
    </Tooltip>
  );
}

export interface TaxBenefitFormProps {
  onSubmit: (values: TaxBenefitFormValues) => void;
  className?: string;
}

export function TaxBenefitForm({ onSubmit, className }: TaxBenefitFormProps) {
  const handleSubmit = useCallback((values: TaxBenefitFormValues) => onSubmit(values), [onSubmit]);

  return (
    <Formik initialValues={INITIAL_VALUES} validationSchema={schema} onSubmit={handleSubmit}>
      {({ errors, setFieldValue, values, submitForm }) => (
        <Form className={classNames(className)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#111827', marginBottom: '8px' }}>
                소득 구간
              </label>
              <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                <Button
                  variant={values.incomeLevel === 'below55m' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setFieldValue('incomeLevel', 'below55m');
                    submitForm();
                  }}
                  sx={{ flex: 1, fontSize: '0.875rem', background: values.incomeLevel === 'below55m' ? '#000033' : 'transparent', '&:hover': { background: values.incomeLevel === 'below55m' ? '#001166' : 'transparent' } }}
                >
                  5,500만원 이하 (16.5%)
                </Button>
                <Button
                  variant={values.incomeLevel === 'above55m' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setFieldValue('incomeLevel', 'above55m');
                    submitForm();
                  }}
                  sx={{ flex: 1, fontSize: '0.875rem', background: values.incomeLevel === 'above55m' ? '#000033' : 'transparent', '&:hover': { background: values.incomeLevel === 'above55m' ? '#001166' : 'transparent' } }}
                >
                  5,500만원 초과 (13.2%)
                </Button>
              </Box>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#111827', marginBottom: '8px' }}>
                ISA 유형
              </label>
              <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                <Button
                  variant={values.isaType === 'general' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setFieldValue('isaType', 'general');
                    submitForm();
                  }}
                  sx={{ flex: 1, fontSize: '0.875rem', background: values.isaType === 'general' ? '#000033' : 'transparent', '&:hover': { background: values.isaType === 'general' ? '#001166' : 'transparent' } }}
                >
                  일반형 (비과세 200만)
                </Button>
                <Button
                  variant={values.isaType === 'youth' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setFieldValue('isaType', 'youth');
                    submitForm();
                  }}
                  sx={{ flex: 1, fontSize: '0.875rem', background: values.isaType === 'youth' ? '#000033' : 'transparent', '&:hover': { background: values.isaType === 'youth' ? '#001166' : 'transparent' } }}
                >
                  서민·청년형 (비과세 400만)
                </Button>
              </Box>
            </div>

            <Field name="irpContribution">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={<span style={{ display: 'flex', alignItems: 'center' }}>연간 IRP 납입액 (원)<FieldTooltip text="연간 최대 900만 원까지 세액공제 대상입니다. 퇴직연금(DC형)과 합산 기준입니다." /></span>}
                  placeholder="9,000,000"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.irpContribution && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.irpContribution}</div>}

            <Field name="isaAnnualContribution">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={<span style={{ display: 'flex', alignItems: 'center' }}>연간 ISA 납입액 (원)<FieldTooltip text="연간 최대 2,000만 원까지 납입 가능합니다. 만기 후 IRP 이체 시 추가 혜택이 있습니다." /></span>}
                  placeholder="10,000,000"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.isaAnnualContribution && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.isaAnnualContribution}</div>}

            <Field name="expectedIsaProfit">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={<span style={{ display: 'flex', alignItems: 'center' }}>5년 ISA 예상 수익 (원)<FieldTooltip text="5년간 ISA 계좌에서 발생할 것으로 예상되는 이자·배당 총수익입니다. 비과세 한도와 비교해 절세액을 계산합니다." /></span>}
                  placeholder="2,000,000"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.expectedIsaProfit && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.expectedIsaProfit}</div>}

            <Field name="projectionYears">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={<span style={{ display: 'flex', alignItems: 'center' }}>누적 계산 기간 (년)<FieldTooltip text="IRP·ISA를 같은 방식으로 계속 활용할 경우 몇 년간의 절세 효과를 계산할지 설정합니다." /></span>}
                  placeholder="10"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.projectionYears && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.projectionYears}</div>}
          </div>

          <div style={{ marginTop: '24px' }}>
            <Button variant="contained" type="submit" fullWidth size="large" sx={{ background: '#000033', '&:hover': { background: '#001166' } }}>
              세제혜택 계산하기
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
}
